import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import clsx from "clsx";

import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import PauseCircleFilledIcon from "@material-ui/icons/PauseCircleFilled";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import NoContent from "components/NoContent";
import { useRacehorse360Api } from "hooks/api";
import {
  checkChrome,
  checkIPhone,
  checkMobileChrome
} from "common/devicesAndBrowsers";
import useStyles from "./styles";

interface IProps {
  trackOpticsVideoId: string;
  videoType: racehorse360.TrackOpticsVideoType;
}

const NoContentMessage = "No Video Found";

const VideoPlayer = (props: IProps) => {
  const { trackOpticsVideoId, videoType } = props;

  const classes = useStyles();
  const playerRef = useRef(null);

  const isChrome = checkChrome();
  const isMobileChrome = checkMobileChrome();
  const isIPhone = checkIPhone();

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isRequestFailed, setIsRequestFailed] = useState(false);
  const [showControls, setShowControls] = useState(isChrome);
  const [removeCustomButton, setRemoveCustomButton] = useState(false);

  const { usePullTrackOpticsVideoFileSignedUrl } = useRacehorse360Api();

  const shouldHideCustomPlayButton = [isVideoPlaying, isMobileChrome].some(
    Boolean
  );

  const {
    data,
    isLoading: isVideoLinkLoading,
    isFetching: isVideoLinkFetching
  } = usePullTrackOpticsVideoFileSignedUrl(
    {
      trackOpticsVideoId: trackOpticsVideoId,
      videoType
    },
    {
      onSuccess: () => {
        setIsRequestFailed(false);
      },
      onError: () => {
        setIsRequestFailed(true);
      },
      gcTime: 0
    }
  );

  const onPlay = () => {
    setIsVideoPlaying(true);
    isIPhone && setRemoveCustomButton(true);
  };

  const onPause = () => {
    setIsVideoPlaying(false);
  };

  const handlePlayVideo = () => {
    if (playerRef?.current) {
      const internalPlayer = playerRef.current.getInternalPlayer();

      isVideoPlaying ? internalPlayer.pause() : internalPlayer.play();

      setShowControls(true);
    }
  };

  const handleFullScreenChange = () => {
    document.fullscreenElement
      ? playerRef.current.getInternalPlayer().play()
      : document.exitFullscreen();
  };

  const actionButtonIcon = isVideoPlaying ? (
    <PauseCircleFilledIcon className={classes.actionButtonIcon} />
  ) : (
    <PlayCircleFilledIcon className={classes.actionButtonIcon} />
  );

  const isVideoLinkGetting = [isVideoLinkLoading, isVideoLinkFetching].some(
    Boolean
  );

  useEffect(() => {
    setShowControls(isChrome);
    setIsVideoPlaying(false);
  }, [data?.signedUrl, isChrome]);

  useEffect(() => {
    isIPhone && setRemoveCustomButton(false);
  }, [videoType, trackOpticsVideoId, isIPhone]);

  useEffect(() => {
    if (isIPhone) {
      document.addEventListener("fullscreenchange", handleFullScreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  if (isVideoLinkGetting) return <Loader className={classes.loader} />;

  if (isRequestFailed) {
    return (
      <NoContent className={classes.noContent}>{NoContentMessage}</NoContent>
    );
  }

  return (
    <div className={classes.wrapVideoPlayer}>
      <div
        className={clsx(classes.actionButton, {
          hidden: shouldHideCustomPlayButton,
          none: removeCustomButton
        })}
        role={"button"}
        tabIndex={0}
        aria-label={"custom play video button"}
        onClick={handlePlayVideo}
      >
        {actionButtonIcon}
      </div>
      <ReactPlayer
        ref={playerRef}
        url={[{ src: data?.signedUrl, type: "video/mp4" }]}
        controls={showControls}
        width={"100%"}
        height={"100%"}
        onPlay={onPlay}
        onPause={onPause}
        playsinline={isIPhone}
      />
    </div>
  );
};

export default React.memo(VideoPlayer);
